import type { McpConnectorPreset } from '@kanbu/schema/contracts/tools';
import { McpAuthMode, McpConnectionStatus } from '@kanbu/schema/enums';
import { useLingui, Trans } from '@lingui/react/macro';
import { Badge, Button, toast } from '@utima/ui';
import { Plus, RefreshCw, Plug, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { trpc, type RouterOutputs } from '@/services/trpc';

import { McpConnectorCatalog } from './McpConnectorCatalog';
import {
  McpIntegrationDialog,
  type McpIntegrationPrefill,
} from './McpIntegrationDialog';
import { useMcpIntegrationMutation } from '../hooks/useMcpIntegrationMutation';
import { useMcpOAuthConnect } from '../hooks/useMcpOAuthConnect';

type McpIntegrationRow = RouterOutputs['mcpIntegrations']['findAll'][number];

const statusVariant: Record<
  McpConnectionStatus,
  'success' | 'muted' | 'warning' | 'danger'
> = {
  [McpConnectionStatus.Connected]: 'success',
  [McpConnectionStatus.NotConnected]: 'muted',
  [McpConnectionStatus.Expired]: 'warning',
  [McpConnectionStatus.Error]: 'danger',
};

export function McpIntegrationsTab() {
  const { t } = useLingui();
  const { data: integrations, isLoading } =
    trpc.mcpIntegrations.findAll.useQuery();
  const { remove, testConnection, syncTools } = useMcpIntegrationMutation();
  const { connect } = useMcpOAuthConnect();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<McpIntegrationRow | undefined>();
  const [prefill, setPrefill] = useState<McpIntegrationPrefill | undefined>();

  const openCreate = () => {
    setEditing(undefined);
    setPrefill(undefined);
    setDialogOpen(true);
  };

  const openEdit = (integration: McpIntegrationRow) => {
    setEditing(integration);
    setPrefill(undefined);
    setDialogOpen(true);
  };

  const handleSelectPreset = (preset: McpConnectorPreset) => {
    setEditing(undefined);
    setPrefill({
      name: preset.name,
      serverUrl: preset.serverUrl,
      authMode: McpAuthMode.McpOAuth,
      connectorId: preset.id,
    });
    setDialogOpen(true);
  };

  const handleTest = async (integration: McpIntegrationRow) => {
    try {
      const result = await testConnection.mutateAsync({ id: integration.id });

      if (result.status === 'connected') {
        toast.success(t`Connected — ${result.tools.length} tools available.`);
      } else if (result.status === 'auth_required') {
        toast.info(t`Authorization required — opening sign-in…`);
        await connect(integration.id);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t`Test failed.`);
    }
  };

  const handleSync = async (integration: McpIntegrationRow) => {
    try {
      const result = await syncTools.mutateAsync({ id: integration.id });

      if (result.status === 'synced') {
        toast.success(t`Synced ${result.toolCount} tools.`);
      } else if (result.status === 'auth_required') {
        toast.info(t`Authorization required — opening sign-in…`);
        await connect(integration.id);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t`Sync failed.`);
    }
  };

  const handleDelete = async (integration: McpIntegrationRow) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t`Delete integration "${integration.name}"?`)) {
      return;
    }

    await remove.mutateAsync({ id: integration.id });
    toast.success(t`Integration deleted.`);
  };

  const items = integrations ?? [];

  return (
    <div className='space-y-4'>
      <McpConnectorCatalog onSelect={handleSelectPreset} />

      <div className='flex items-center justify-between'>
        <span className='text-sm font-medium'>
          <Trans>Your integrations</Trans>
        </span>
        <Button size='sm' onClick={openCreate}>
          <Plus className='size-4' />
          <Trans>Add custom server</Trans>
        </Button>
      </div>

      {isLoading && (
        <p className='text-sm text-muted-fg'>
          <Trans>Loading…</Trans>
        </p>
      )}

      {!isLoading && items.length === 0 && (
        <div className='rounded-radius border border-dashed border-border p-8 text-center text-sm text-muted-fg'>
          <Trans>
            No MCP integrations yet. Connect an MCP server to give your agents
            new tools.
          </Trans>
        </div>
      )}

      {items.length > 0 && (
        <div className='overflow-hidden rounded-radius border border-border'>
          <table className='w-full text-sm'>
            <thead className='bg-muted/40 text-left text-muted-fg'>
              <tr>
                <th className='p-3 font-medium'>
                  <Trans>Name</Trans>
                </th>
                <th className='p-3 font-medium'>
                  <Trans>Server</Trans>
                </th>
                <th className='p-3 font-medium'>
                  <Trans>Status</Trans>
                </th>
                <th className='p-3 font-medium'>
                  <Trans>Tools</Trans>
                </th>
                <th className='p-3 font-medium'>
                  <Trans>Scope</Trans>
                </th>
                <th className='p-3 text-right font-medium'>
                  <Trans>Actions</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map(integration => (
                <tr key={integration.id} className='border-t border-border'>
                  <td className='p-3'>
                    <div className='font-medium'>{integration.name}</div>
                    {!integration.isActive && (
                      <Badge variant='muted' size='xs'>
                        <Trans>Inactive</Trans>
                      </Badge>
                    )}
                  </td>
                  <td className='max-w-56 truncate p-3 text-muted-fg'>
                    {integration.serverUrl}
                  </td>
                  <td className='p-3'>
                    <Badge
                      variant={statusVariant[integration.connectionStatus]}
                      size='xs'
                    >
                      {integration.connectionStatus}
                    </Badge>
                  </td>
                  <td className='p-3'>{integration.toolCount ?? '—'}</td>
                  <td className='p-3'>
                    {integration.availableToAllChats ? (
                      <Trans>All chats</Trans>
                    ) : (
                      <Trans>{integration.chatIds?.length ?? 0} chats</Trans>
                    )}
                  </td>
                  <td className='p-3'>
                    <div className='flex justify-end gap-1'>
                      <Button
                        size='sm'
                        variant='ghost'
                        title={t`Test connection`}
                        loading={
                          testConnection.isPending &&
                          testConnection.variables?.id === integration.id
                        }
                        onClick={() => handleTest(integration)}
                      >
                        <Plug className='size-4' />
                      </Button>
                      <Button
                        size='sm'
                        variant='ghost'
                        title={t`Sync tools`}
                        loading={
                          syncTools.isPending &&
                          syncTools.variables?.id === integration.id
                        }
                        onClick={() => handleSync(integration)}
                      >
                        <RefreshCw className='size-4' />
                      </Button>
                      <Button
                        size='sm'
                        variant='ghost'
                        title={t`Edit`}
                        onClick={() => openEdit(integration)}
                      >
                        <Pencil className='size-4' />
                      </Button>
                      <Button
                        size='sm'
                        variant='ghost'
                        title={t`Delete`}
                        onClick={() => handleDelete(integration)}
                      >
                        <Trash2 className='size-4 text-danger' />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <McpIntegrationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        integration={editing}
        prefill={prefill}
      />
    </div>
  );
}
